var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import HLS from 'hls.js';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { SizeMe } from 'react-sizeme';
import Button from '../../../../core/components/Button';
import ProgressBar from '../../../../core/components/ProgressBar';
import Skeleton from '../../../../core/components/Skeleton';
import LiveBadge from '../../../../social/components/LiveBadge';
import { ExclamationCircle, Play, Remove } from '../../../../icons';
export const VideoContainer = styled.div `
  position: relative;
  display: inline-block;
  min-width: 2em;
  min-height: 2em;
  width: 100%;
  height: 100%;
  border: ${({ theme, border }) => border && `1px solid ${theme.palette.base.shade4}`};
  border-radius: 4px;
  overflow: hidden;

  .darken {
    opacity: 0.4;
  }
`;
export const Content = styled.div `
  position: relative;
  width: 100%;
  height: 100%;
`;
const VideoPreviewContainerStyles = css `
  display: block;
  width: 100%;
  height: 100%;
  object-fit: ${({ mediaFit }) => mediaFit !== null && mediaFit !== void 0 ? mediaFit : 'cover'};
  object-position: center;
`;
export const VideoPreview = styled(React.forwardRef((_a, ref) => {
    var { src, mimeType, mediaFit } = _a, props = __rest(_a, ["src", "mimeType", "mediaFit"]);
    return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    React.createElement("video", Object.assign({ controls: true, controlsList: "nodownload" }, props, { ref: ref, "data-qa-anchor": "video-preview" }),
        React.createElement("source", { src: src, type: mimeType }),
        React.createElement("p", null, "Your browser does not support this format of video. Please try again later once the server transcodes the video into an playable format(mp4).")));
})) `
  ${VideoPreviewContainerStyles}
  cursor: pointer;
`;
export const SkeletonWrapper = styled.div `
  ${VideoPreviewContainerStyles};

  display: flex;
  align-items: center;
  justify-content: center;
`;
const VideoSkeleton = () => (React.createElement(SizeMe, { monitorHeight: true }, ({ size }) => {
    const minSize = Math.min(size.width, size.height);
    return (React.createElement(SkeletonWrapper, null,
        React.createElement(Skeleton, { height: minSize, width: minSize, style: { display: 'block' } })));
}));
export const RemoveButton = styled(Button).attrs({
    variant: 'secondary',
    children: React.createElement(Remove, null),
}) `
  position: absolute;
  top: 0.5em;
  right: 0.5em;
`;
export const CircleIcon = styled(ExclamationCircle).attrs({ width: 24, height: 24 }) `
  z-index: 2;
  opacity: 0.7;
`;
export const RetryButton = styled(Button).attrs({
    variant: 'secondary',
    children: React.createElement(CircleIcon, null),
}) `
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
`;
const ButtonContainer = styled.div `
  display: flex;
`;
export const PlayIcon = styled(Play) `
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;
export const LiveIcon = styled(LiveBadge) `
  position: absolute;
  top: 1rem;
  left: 1rem;
`;
const Video = ({ url, progress, mediaFit, noBorder, onRemove, isRejected, onRetry, mimeType, autoPlay, isLive, }) => {
    const removeCallback = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove && onRemove();
    }, [onRemove]);
    const retryCallback = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        onRetry && onRetry();
    }, [onRetry]);
    const [videoEl, setVideoEl] = useState();
    useEffect(() => {
        if (!videoEl || !url || !url.includes('m3u8')) {
            return;
        }
        if (!(videoEl === null || videoEl === void 0 ? void 0 : videoEl.canPlayType('application/vnd.apple.mpegurl')) && HLS.isSupported()) {
            const hls = new HLS();
            hls.loadSource(url);
            hls.attachMedia(videoEl);
            hls.on(HLS.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case HLS.ErrorTypes.NETWORK_ERROR:
                            hls.startLoad();
                            break;
                        case HLS.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError();
                            break;
                        default:
                            // hls.destroy();
                            break;
                    }
                }
            });
            return () => hls.destroy();
        }
    }, [videoEl, isLive, url]);
    return (React.createElement(VideoContainer, { border: !noBorder },
        React.createElement(Content, null,
            url ? (React.createElement(VideoPreview, { key: url, ref: (el) => setVideoEl(el), className: !!isRejected && 'darken', mediaFit: mediaFit, mimeType: mimeType, src: url, autoPlay: autoPlay })) : (React.createElement(VideoSkeleton, null)),
            React.createElement(ButtonContainer, null,
                isLive && React.createElement(LiveIcon, null),
                !!isRejected && React.createElement(RetryButton, { onClick: retryCallback }),
                !!onRemove && React.createElement(RemoveButton, { onClick: removeCallback }))),
        !Number.isNaN(progress) && React.createElement(ProgressBar, { progress: progress * 100 })));
};
Video.propTypes = {
    url: PropTypes.string,
    progress: PropTypes.number,
    mediaFit: PropTypes.oneOf(['cover', 'contain']),
    noBorder: PropTypes.bool,
    isRejected: PropTypes.bool,
    mimeType: PropTypes.string,
    autoPlay: PropTypes.bool,
    isLive: PropTypes.bool,
    onRemove: PropTypes.func,
    onRetry: PropTypes.func,
};
Video.defaultProps = {
    url: undefined,
    progress: -1,
    onRemove: undefined,
    isRejected: false,
    onRetry: undefined,
    mimeType: undefined,
    autoPlay: false,
    isLive: false,
};
export default Video;
//# sourceMappingURL=styles.js.map